// Supabase client configuration
// Uses runtime hostname detection (envConfig) instead of .env files
// which are not reliably persisted by Bolt.new
// UPDATED: AssignedTaskOccurrence now includes 4 snapshot columns for frozen template metadata
// UPDATED: TaskItem interface now includes text_input_label (optional string)
// UPDATED: TaskItem and AssignedTaskItem now include text_input_type and kpi_definition_id for KPI system v1

import { createClient } from '@supabase/supabase-js';
import { envConfig } from './envConfig';

export const supabase = createClient(envConfig.supabaseUrl, envConfig.supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
  },
});

// Types for database tables
export interface Site {
  id: string;
  name: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export type UserRole = 'operator' | 'manager' | 'director' | 'admin';

export interface User {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  site_id: string | null;
  auth_email: string;
  last_activity: string;
  is_active: boolean;
  must_change_password: boolean;
  password_reset_at: string | null;
  password_reset_by: string | null;
  created_at: string;
  updated_at: string;
}

// Table de jointure pour la relation many-to-many users <-> sites
export interface UserSite {
  id: string;
  user_id: string;
  site_id: string;
  created_at: string;
}

// Type étendu avec les sites multiples pour l'affichage
export interface UserWithSites extends User {
  sites?: Site[];
}

export interface TaskTemplate {
  id: string;
  name: string;
  description: string | null;
  estimated_duration_minutes: number | null;
  service_id: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface TaskItem {
  id: string;
  template_id: string;
  title: string;
  order_index: number;
  created_at: string;
  // Text input configuration
  requires_text_input?: boolean;
  text_input_label?: string;
  text_input_placeholder?: string;
  is_text_input_required?: boolean;
  // KPI system v1
  text_input_type?: 'text' | 'number';
  kpi_definition_id?: string | null;
}

export interface RecurrenceRule {
  id: string;
  template_id: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  days_of_week: number[];
  day_of_month: number | null;
  week_of_month: number | null;
  interval: number;
  month_of_year: number | null;
  created_at: string;
}

export interface AssignedTask {
  id: string;
  template_id: string | null;
  assigned_to_user_id: string;
  site_id: string;
  assigned_date: string;
  scheduled_time: string | null;
  duration_override: number | null;
  status: 'not_started' | 'completed' | 'overdue';
  validated_at: string | null;
  validated_by: string | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
  // Recurrence fields
  is_recurring: boolean;
  recurrence_rule_id: string | null;
  recurrence_start_date: string | null;
  recurrence_end_date: string | null;
  parent_task_id: string | null;
  // Reassignment rule fields (Option A)
  reassignment_from_date: string | null;
  reassignment_user_id: string | null;
  reassignment_scheduled_time: string | null;
}

export interface AssignedTaskItem {
  id: string;
  assigned_task_id: string;
  title: string;
  order_index: number;
  created_at: string;
  // Execution data filled by operators
  text_value?: string;
  completed_at?: string;
  is_completed?: boolean;
  // KPI system v1
  text_input_type?: 'text' | 'number';
  kpi_definition_id?: string | null;
}

export interface AssignedTaskOccurrence {
  id: string;
  assigned_task_id: string;
  occurrence_date: string;
  status: 'not_started' | 'completed' | 'overdue' | null;
  is_cancelled: boolean;
  scheduled_time_override: string | null;
  duration_override: number | null;
  assigned_to_user_id_override: string | null;
  notes: string | null;
  template_name: string | null;
  template_description: string | null;
  template_estimated_duration_minutes: number | null;
  template_service_id: string | null;
  created_at: string;
  updated_at: string;
}

// Utility functions
export function getFullName(user: { first_name: string; last_name: string }): string {
  return `${user.first_name} ${user.last_name}`.trim();
}

export function getUserInitials(user: { first_name: string; last_name: string }): string {
  const lastInitial = user.last_name?.[0] || '';
  const firstInitial = user.first_name?.[0] || '';
  return (lastInitial + firstInitial).toUpperCase();
}
